import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { conf as mainConf } from 'apps/config.yaml';
import { imageSrc } from 'apps/file/utils';

import { Link } from 'core/routing';
import Icon from 'components/Icon';
import Paragraph from 'components/Paragraph';
import Tooltip from 'components/Tooltip';
import Like from 'apps/content/views/like';
import { PublishedBy } from 'apps/story/views/publishedBy';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import { Date } from 'apps/components/date';

import {
  MediumCard,
  MediumCardImg,
  MediumInfoWrapper,
  CardFooter,
  CardFooterList,
  CardFooterItem,
  CardFooterButton,
  pseudoCardStyles,
} from 'legacyComponents/cards/styles';

import styled from 'styled-components';

export const StoryCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns }) => {
    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='story' />;
    }

    const {
      avatar,
      title,
      creatorProfile,
      dateCreated,
      bodySnippet,
      readLength,
      commentsCount,
      slug,
      spacePath,
      content,
    } = cardObject;
    let spaceType = 'profile';
    if (spacePath[0] === '+') {
      spaceType = 'initiative';
    }
    const name = `${spaceType}.story`;
    return (
      <MediumCard
        noMargin={noMargin}
        data-intercom-target={cardNo === 0 ? 'organisation-stories-list' : ''}
      >
        <Link
          name={name}
          params={{ [spaceType]: spacePath, slug }}
          content={content}
        >
          <MediumCardImg
            src={imageSrc(
              avatar,
              cardObject.imageFallbackPath,
              mainConf.imgSizes.story.card,
            )}
            alt={title}
          />
        </Link>
        <MediumInfoWrapper>
          <Link
            name={name}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
          >
            <Title>{title}</Title>
          </Link>
          <Paragraph size='small' variant='tertiary'>
            <PublishedBy
              spaceType={spaceType}
              spacePath={spacePath}
              creatorProfile={creatorProfile}
            />
            <DateStyled value={dateCreated} />
          </Paragraph>
          <Preview>{bodySnippet}</Preview>
        </MediumInfoWrapper>
        <CardFooter>
          <Tooltip />
          <CardFooterList>
            <CardFooterItem align='left'>
              <Icon name='time' data-tip={`${readLength} min read`} />
              <Trans count={readLength}>{{ readLength }} min</Trans>
            </CardFooterItem>
            <CardFooterItem>
              <Icon name='view' data-tip='Views' />
              {content.pageViewsFormatted}
            </CardFooterItem>
            <CardFooterItem>
              <Icon name='comment' data-tip='Comments' />
              <Link
                name={name}
                params={{ [spaceType]: spacePath, slug }}
                content={content}
              >
                {commentsCount}
              </Link>
            </CardFooterItem>
            <CardFooterItem>
              <Like content={cardObject.content} />
            </CardFooterItem>
          </CardFooterList>
          <Link
            name={name}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
          >
            <CardFooterButton icon='arrow_right' appearance='plain' />
          </Link>
        </CardFooter>
      </MediumCard>
    );
  }),
);

const Title = styled.h3`
  font-size: ${props => props.theme.font.size.medium};
  color: ${props => props.theme.color.wfBlack};
  line-height: ${props => props.theme.font.lineHeight.medium};
  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const Preview = styled(Paragraph)`
  margin-top: ${props => props.theme.spacing.default};
  line-height: 1.65;
`;

const DateStyled = styled(Date)`
  &::before {
    ${pseudoCardStyles}
  }
`;

StoryCard.propTypes = {
  cardObject: PropTypes.object,
  cardColumns: PropTypes.number,
  cardNo: PropTypes.number,
  t: PropTypes.func,
};
